import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api/crm`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('crm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      localStorage.removeItem('crm_token');
      localStorage.removeItem('crm_user');
      if (!window.location.pathname.startsWith('/crm/login')) window.location.href = '/crm/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password) => api.post('/auth/register', { name, email, password }),
  getMe: () => api.get('/auth/me'),
};

export const candidatesAPI = {
  getAll: (params) => api.get('/candidates', { params }),
  getById: (id) => api.get(`/candidates/${id}`),
  create: (data, force = false) => api.post(`/candidates${force ? '?force=true' : ''}`, data),
  update: (id, data) => api.put(`/candidates/${id}`, data),
  delete: (id) => api.delete(`/candidates/${id}`),
  bulkDelete: (ids) => api.post('/candidates/bulk-delete', ids),
  getFilterOptions: () => api.get('/candidates/filters/options'),
  proximitySearch: (params) => api.get('/candidates/proximity', { params }),
  verifyLocation: (id) => api.put(`/candidates/${id}/verify-location`),
  updatePostcode: (id, postcode) => api.put(`/candidates/${id}/update-postcode?postcode=${encodeURIComponent(postcode)}`),
  bulkUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/candidates', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  contactoutUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/contactout', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  bulkDelete: (ids) => api.post('/clients/bulk-delete', ids),
  getFilterOptions: () => api.get('/clients/filters/options'),
  proximitySearch: (params) => api.get('/clients/proximity', { params }),
  backfillPostcodes: () => api.post('/clients/backfill-postcodes'),
  bulkUpload: (file, addedBy = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (addedBy) formData.append('added_by', addedBy);
    return api.post('/upload/clients', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const jobsAPI = {
  getAll: (params) => api.get('/jobs', { params }),
  getById: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
};

export const pipelineAPI = {
  getAll: (params) => api.get('/pipeline', { params }),
  add: (data) => api.post('/pipeline', data),
  update: (id, data) => api.put(`/pipeline/${id}`, data),
  remove: (id) => api.delete(`/pipeline/${id}`),
};

export const activitiesAPI = {
  getAll: (params) => api.get('/activities', { params }),
  getByEntity: (entityType, entityId) => api.get(`/activities/entity/${entityType}/${entityId}`),
  create: (data) => api.post('/activities', data),
};

export const callbacksAPI = {
  getAll: (params) => api.get('/callbacks', { params }),
  create: (data) => api.post('/callbacks', data),
  update: (id, data) => api.put(`/callbacks/${id}`, data),
  delete: (id) => api.delete(`/callbacks/${id}`),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRevenue: (params) => api.get('/dashboard/revenue', { params }),
  getExchangeRates: () => api.get('/exchange-rates'),
};

export const reportsAPI = {
  getRevenueOverTime: (months = 12) => api.get(`/reports/revenue-over-time?months=${months}`),
  getPipelineSummary: () => api.get('/reports/pipeline-summary'),
  getMissedOpportunities: () => api.get('/reports/missed-opportunities'),
  getTopLocations: () => Promise.resolve({ data: [] }),
  getTopClients: () => Promise.resolve({ data: [] }),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { new_password: newPassword }),
  delete: (id) => api.delete(`/users/${id}`),
};

export const cvAPI = {
  upload: (candidateId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/candidates/${candidateId}/cv`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getInfo: (candidateId) => api.get(`/candidates/${candidateId}/cv`),
  delete: (candidateId) => api.delete(`/candidates/${candidateId}/cv`),
  getDownloadUrl: (filename) => `${API_BASE}/uploads/cvs/${filename}`,
  parseCv: (candidateId) => api.post(`/candidates/${candidateId}/parse-cv`),
  getSummary: (candidateId) => api.get(`/candidates/${candidateId}/cv-summary`),
  updateSummary: (candidateId, data) => api.put(`/candidates/${candidateId}/cv-summary`, data),
  getOnePagerUrl: (candidateId, brand = 'brightridge', costToCompany = '') =>
    `${API_BASE}/candidates/${candidateId}/one-pager?brand=${brand}${costToCompany ? `&cost_to_company=${encodeURIComponent(costToCompany)}` : ''}`,
  getFullCvPreview: (candidateId, anonymizeEmployers = true) =>
    api.post(`/candidates/${candidateId}/full-cv-preview?anonymize_employers=${anonymizeEmployers}`),
  generateFullCv: (candidateId, selections) =>
    api.post(`/candidates/${candidateId}/full-cv`, selections, { responseType: 'blob' }),
  getReferences: (candidateId) => api.get(`/candidates/${candidateId}/references`),
  updateReferences: (candidateId, references) => api.put(`/candidates/${candidateId}/references`, references),
};

export const workstreamsAPI = {
  getAll: (params = {}) => api.get('/workstreams', { params }),
  getById: (id) => api.get(`/workstreams/${id}`),
  create: (data) => api.post('/workstreams', data),
  update: (id, data) => api.put(`/workstreams/${id}`, data),
  delete: (id) => api.delete(`/workstreams/${id}`),
  getRecords: (id) => api.get(`/workstreams/${id}/records`),
  addRecords: (id, recordIds) => api.post(`/workstreams/${id}/records`, { record_ids: recordIds }),
  removeRecord: (wid, rid) => api.delete(`/workstreams/${wid}/records/${rid}`),
  bulkUpload: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/workstreams/${id}/bulk-upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getAnalytics: (id) => api.get(`/workstreams/${id}/analytics`),
  getDiagnostics: (id) => api.get(`/workstreams/${id}/diagnostics`),
};

export const constantsAPI = {
  getAll: () => api.get('/constants'),
};

export const favoritesAPI = {
  getAll: (entityType = null) => api.get('/favorites', { params: entityType ? { entity_type: entityType } : {} }),
  add: (entityType, entityId) => api.post(`/favorites/${entityType}/${entityId}`),
  remove: (entityType, entityId) => api.delete(`/favorites/${entityType}/${entityId}`),
  check: (entityType, entityId) => api.get(`/favorites/check/${entityType}/${entityId}`),
};

export const eorClientsAPI = {
  getAll: (params = {}) => api.get('/eor/clients', { params }),
  getById: (id) => api.get(`/eor/clients/${id}`),
  create: (data) => api.post('/eor/clients', data),
  update: (id, data) => api.put(`/eor/clients/${id}`, data),
  delete: (id) => api.delete(`/eor/clients/${id}`),
};

export const eorEmployeesAPI = {
  getAll: (params = {}) => api.get('/eor/employees', { params }),
  getById: (id) => api.get(`/eor/employees/${id}`),
  create: (data) => api.post('/eor/employees', data),
  update: (id, data) => api.put(`/eor/employees/${id}`, data),
  delete: (id) => api.delete(`/eor/employees/${id}`),
  hireCandidate: (candidateId, data) => api.post(`/candidates/${candidateId}/hire-eor`, data),
};

export const eorDashboardAPI = {
  getStats: () => api.get('/eor/dashboard'),
};

export const zohoAPI = {
  getAuthUrl: () => api.get('/zoho/auth-url'),
  callback: (code, location = 'in') => api.post('/auth/zoho/callback', { code, location }),
  getStatus: () => api.get('/zoho/status'),
  disconnect: () => api.delete('/zoho/disconnect'),
  sendEmail: (data) => api.post('/zoho/send-email', data),
  updateSenderName: (sender_name) => api.put('/zoho/sender-name', { sender_name }),
};

export const emailTemplatesAPI = {
  getAll: () => api.get('/email-templates'),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
};

export const emailSignaturesAPI = {
  getAll: () => api.get('/email-signatures'),
  create: (data) => api.post('/email-signatures', data),
  update: (id, data) => api.put(`/email-signatures/${id}`, data),
  delete: (id) => api.delete(`/email-signatures/${id}`),
};

export const callPrepAPI = {
  getRoles: () => axios.get(`${BACKEND_URL}/api/admin/call-prep/crm-roles`, { headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` } }),
  getSheet: (roleId) => axios.get(`${BACKEND_URL}/api/admin/call-prep/crm-sheet/${roleId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` } }),
  saveAnswers: (candidateId, answers) => api.put(`/candidates/${candidateId}/call-prep`, answers),
  getAnswers: (candidateId) => api.get(`/candidates/${candidateId}/call-prep`),
};

export const plannerAPI = {
  getDashboard: (params) => api.get('/planner/dashboard', { params }),
  getKpiDefinitions: () => api.get('/planner/kpi-definitions'),
  // Tasks
  getTasks: (params) => api.get('/planner/tasks', { params }),
  createTask: (data) => api.post('/planner/tasks', data),
  updateTask: (id, data) => api.put(`/planner/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/planner/tasks/${id}`),
  rolloverOverdue: () => api.post('/planner/tasks/rollover-overdue'),
  // KPIs
  getKpis: (params) => api.get('/planner/kpis', { params }),
  upsertKpi: (data) => api.post('/planner/kpis', data),
  // Goals
  getGoals: (params) => api.get('/planner/goals', { params }),
  upsertGoal: (data) => api.post('/planner/goals', data),
  // Team
  getTeam: () => api.get('/planner/team'),
  // Analytics
  getAnalytics: (params) => api.get('/planner/analytics', { params }),
  getLeaderboard: (params) => api.get('/planner/leaderboard', { params }),
  // Templates
  getTemplates: () => api.get('/planner/templates'),
  createTemplate: (data) => api.post('/planner/templates', data),
  deleteTemplate: (id) => api.delete(`/planner/templates/${id}`),
  applyTemplate: (id, date) => api.post(`/planner/templates/${id}/apply`, null, { params: { date } }),
  // Focus
  saveFocus: (data) => api.post('/planner/focus', data),
};


export default api;

export const eorCandidatesAPI = {
  list: (params) => api.get('/eor/candidates', { params }),
  filterOptions: () => api.get('/eor/candidates/filters/options'),
  get: (id) => api.get(`/eor/candidates/${id}`),
  update: (id, data) => api.patch(`/eor/candidates/${id}`, data),
  remove: (id) => api.delete(`/eor/candidates/${id}`),
  bulkDelete: (ids) => api.post('/eor/candidates/bulk-delete', { ids }),
  uploadCv: (id, file) => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return api.post(`/eor/candidates/${id}/cv`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteCv: (id) => api.delete(`/eor/candidates/${id}/cv`),
  downloadCv: (filename) => api.get(`/eor/uploads/cvs/${filename}`, { responseType: 'blob' }),
  getCallPrep: (id) => api.get(`/eor/candidates/${id}/call-prep`),
  saveCallPrep: (id, answers) => api.put(`/eor/candidates/${id}/call-prep`, answers),
};

export const questionTemplatesAPI = {
  list: (serviceLine) => api.get('/question-templates', { params: { service_line: serviceLine } }),
  create: (data) => api.post('/question-templates', data),
  update: (id, data) => api.put(`/question-templates/${id}`, data),
  remove: (id) => api.delete(`/question-templates/${id}`),
};

export const cvSearchAPI = {
  status: (pool) => api.get('/cv-search/status', { params: { pool } }),
  smart: (query, pool) => api.post('/cv-search/smart', { query, pool }, { timeout: 120000 }),
};
